/**
 * Date/Time Utilities for Ride Scheduling App
 *
 * These utilities handle timezone conversions between:
 * - Backend: UTC storage (ISO strings with Z suffix like "2025-11-03T15:00:00Z")
 * - Frontend: Display in user's browser local timezone
 *
 * Usage:
 * - For DISPLAY: Use formatDateTime() to show times to users in their local timezone
 * - For INPUT: Use parseUserDateTime() to convert user input to UTC ISO string for backend
 */

/**
 * Get the user's current timezone
 * @returns {string} IANA timezone identifier (e.g., "America/New_York", "America/Chicago")
 */
export const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Ensure a datetime string is interpreted as UTC.
 * The backend stores all datetimes as naive UTC, and Pydantic serializes them
 * without a 'Z' suffix (e.g., "2026-02-14T12:00:00"). Without 'Z', JavaScript's
 * Date constructor treats the string as local time. This helper appends 'Z' to
 * naive ISO datetime strings so they are correctly parsed as UTC.
 */
export const ensureUTC = (dateStr) => {
    if (typeof dateStr === 'string' && dateStr.includes('T') &&
        !dateStr.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(dateStr)) {
        return dateStr + 'Z';
    }
    return dateStr;
};

/**
 * Format datetime for display in user's local timezone
 * @param {string} isoDateString - ISO string from backend (e.g., "2025-11-03T15:00:00Z")
 * @param {string} format - Display format: 'full', 'date', 'time', 'short', 'datetime'
 * @returns {string} Formatted date string in user's local timezone
 *
 * Examples (if user is in Eastern time):
 * formatDateTime("2025-11-03T15:00:00Z", "full") → "Nov 3, 2025, 10:00 AM"
 * formatDateTime("2025-11-03T15:00:00Z", "date") → "Nov 3, 2025"
 * formatDateTime("2025-11-03T15:00:00Z", "time") → "10:00 AM"
 */
export const formatDateTime = (isoDateString, format = 'full') => {
    if (!isoDateString) return 'N/A';

    const date = new Date(ensureUTC(isoDateString));

    // Check if date is valid
    if (isNaN(date.getTime())) {
        console.error('Invalid date string:', isoDateString);
        return 'Invalid Date';
    }

    // No explicit timeZone - uses browser's local timezone
    const formats = {
        // Full date and time: "Nov 3, 2025, 9:00 AM"
        full: {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        },
        // Date only: "Nov 3, 2025"
        date: {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        },
        // Time only: "9:00 AM"
        time: {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        },
        // Short format: "Nov 3, 9:00 AM"
        short: {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        },
        // Date and time on separate lines: "Nov 3, 2025\n9:00 AM"
        datetime: {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }
    };

    return date.toLocaleString('en-US', formats[format] || formats.full);
};

/**
 * Parse user input date and time into UTC ISO string for backend
 * Treats user input as their local browser timezone, converts to UTC for storage
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {string} time - Time string in HH:MM format (24-hour)
 * @returns {string} ISO string in UTC for backend (e.g., "2025-11-03T15:00:00.000Z")
 *
 * Example (if user is in Eastern time):
 * parseUserDateTime("2025-11-03", "10:00") → "2025-11-03T15:00:00.000Z" (10 AM Eastern = 3 PM UTC)
 */
export const parseUserDateTime = (date, time) => {
    if (!date || !time) return null;

    try {
        // Create date in user's local timezone
        // JavaScript Date constructor interprets "YYYY-MM-DDTHH:MM" as local time
        const localDate = new Date(`${date}T${time}:00`);

        // Check if date is valid
        if (isNaN(localDate.getTime())) {
            console.error('Invalid date/time:', date, time);
            return null;
        }

        // toISOString() converts to UTC automatically
        return localDate.toISOString();
    } catch (error) {
        console.error('Error parsing date/time:', date, time, error);
        return null;
    }
};

/**
 * Parse Date object to ISO string for backend
 * @param {Date} date - JavaScript Date object
 * @returns {string} ISO string in UTC
 *
 * Example:
 * const date = new Date("2025-11-03T09:00:00");
 * parseDateToISO(date) → "2025-11-03T15:00:00.000Z"
 */
export const parseDateToISO = (date) => {
    if (!date || !(date instanceof Date)) return null;

    // Check if date is valid
    if (isNaN(date.getTime())) {
        console.error('Invalid date object:', date);
        return null;
    }

    return date.toISOString();
};

/**
 * Get current date in user's local timezone formatted for date input (YYYY-MM-DD)
 * @returns {string} Date string in YYYY-MM-DD format
 *
 * Example:
 * getCurrentDateForInput() → "2025-11-03"
 */
export const getCurrentDateForInput = () => {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

/**
 * Get current time in user's local timezone formatted for time input (HH:MM)
 * @returns {string} Time string in HH:MM format (24-hour)
 *
 * Example:
 * getCurrentTimeForInput() → "09:00"
 */
export const getCurrentTimeForInput = () => {
    const now = new Date();

    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
};

/**
 * Format duration between two times
 * @param {string} startISO - Start time ISO string
 * @param {string} endISO - End time ISO string
 * @returns {string} Formatted duration (e.g., "1h 30m")
 */
export const formatDuration = (startISO, endISO) => {
    if (!startISO || !endISO) return 'N/A';

    const start = new Date(ensureUTC(startISO));
    const end = new Date(ensureUTC(endISO));

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'Invalid';
    }

    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
        return `${diffMins}m`;
    }

    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (minutes === 0) {
        return `${hours}h`;
    }

    return `${hours}h ${minutes}m`;
};

/**
 * Check if a datetime is in the past
 * @param {string} isoDateString - ISO string to check
 * @returns {boolean} True if the datetime is in the past
 */
export const isPast = (isoDateString) => {
    if (!isoDateString) return false;

    const date = new Date(ensureUTC(isoDateString));
    const now = new Date();

    return date < now;
};

/**
 * Check if a datetime is today
 * @param {string} isoDateString - ISO string to check
 * @returns {boolean} True if the datetime is today in user's local timezone
 */
export const isToday = (isoDateString) => {
    if (!isoDateString) return false;

    const date = new Date(ensureUTC(isoDateString));
    const now = new Date();

    // Compare dates in user's local timezone
    const dateStr = date.toLocaleDateString('en-US');
    const nowStr = now.toLocaleDateString('en-US');

    return dateStr === nowStr;
};

/**
 * Format relative time (e.g., "in 2 hours", "5 minutes ago")
 * @param {string} isoDateString - ISO string
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (isoDateString) => {
    if (!isoDateString) return 'N/A';

    const date = new Date(ensureUTC(isoDateString));
    const now = new Date();

    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }

    const diffMs = date - now;
    const diffMins = Math.floor(Math.abs(diffMs) / 60000);

    if (diffMins < 1) {
        return 'now';
    }

    if (diffMins < 60) {
        const label = diffMins === 1 ? 'minute' : 'minutes';
        return diffMs > 0 ? `in ${diffMins} ${label}` : `${diffMins} ${label} ago`;
    }

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) {
        const label = diffHours === 1 ? 'hour' : 'hours';
        return diffMs > 0 ? `in ${diffHours} ${label}` : `${diffHours} ${label} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);
    const label = diffDays === 1 ? 'day' : 'days';
    return diffMs > 0 ? `in ${diffDays} ${label}` : `${diffDays} ${label} ago`;
};
